import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Layout-density hook. Mirrors `useFontScale` but drives PADDING/GAP.
 *
 * Storage: `yadidos.layoutDensity` in localStorage.
 * NOTE (security review): stores ONLY a non-sensitive UI preference (one of
 * "compact" / "regular" / "spacious"). Not an auth token, PII, or firm data.
 * Reads are validated via `_clean(...)` — an unexpected value collapses to
 * the default rather than being applied verbatim to the DOM attribute.
 * Server (`/users/me/preferences`) is the source of truth.
 *
 * Values: "compact" | "regular" | "spacious".
 * Applied as `<html data-density="…">`; the CSS override rules live in
 * `index.css` and scale the Tailwind `p-*` / `gap-*` / `space-y-*` utilities.
 * We deliberately do NOT touch typography size here — that's owned by
 * `useFontScale`. Density = whitespace only.
 *
 * Persistence: localStorage first (instant, offline-safe) + `PUT
 * /users/me/preferences {layout_density}` (cross-device). Server value wins
 * on login; the local write always happens so an offline change survives
 * and gets pushed on the next successful sync.
 */
const STORAGE_KEY = "yadidos.layoutDensity";
export const DENSITIES = ["compact", "regular", "spacious"];
const DEFAULT = "regular";

function _clean(v) {
  const s = String(v || "").toLowerCase().trim();
  return DENSITIES.includes(s) ? s : DEFAULT;
}

function _readLocal() {
  try { return _clean(localStorage.getItem(STORAGE_KEY)); }
  catch (_) { return DEFAULT; }
}

function _apply(density) {
  document.documentElement.setAttribute("data-density", density);
}

// Apply on module load so first paint already has the right spacing.
if (typeof window !== "undefined") {
  try { _apply(_readLocal()); } catch (e) { console.warn("layout-density: initial apply failed", e); }
}

export function useLayoutDensity() {
  const { user, mergePreferences } = useAuth();
  const [density, setDensityState] = useState(_readLocal);
  const lastServerRef = useRef(null);

  useEffect(() => { _apply(density); }, [density]);

  // Hydrate from server preference when the auth context settles.
  useEffect(() => {
    const s = user?.preferences?.layout_density;
    if (!s) return;
    const clean = _clean(s);
    if (lastServerRef.current === clean) return;
    lastServerRef.current = clean;
    if (clean !== _readLocal()) {
      try { localStorage.setItem(STORAGE_KEY, clean); } catch (e) { console.warn("layout-density: localStorage write failed (server hydrate)", e); }
      setDensityState(clean);
    }
  }, [user]);

  const setDensity = useCallback((next) => {
    const clean = _clean(next);
    try { localStorage.setItem(STORAGE_KEY, clean); } catch (e) { console.warn("layout-density: localStorage write failed (user set)", e); }
    setDensityState(clean);
    // Sync ref forward so the ensuing mergePreferences re-emit doesn't
    // undo the local change. Fixes Settings-persistence-on-return.
    lastServerRef.current = clean;
    api.put("/users/me/preferences", { layout_density: clean })
      .then(() => mergePreferences?.({ layout_density: clean }))
      .catch((e) => console.warn("layout-density sync failed", e));
  }, [mergePreferences]);

  const syncedToServer = typeof user?.preferences?.layout_density === "string";

  return { density, setDensity, syncedToServer };
}
